import { AdSlot } from "../components/AdSlot";
import { ToolCard } from "../components/ToolCard";
import { ToolWorkspace } from "../components/ToolWorkspace";
import { getToolPageCopy } from "../data/i18n";
import { categoryLabel, getRelatedTools } from "../data/tools";
import { applyPageSeo } from "../lib/seo";
import { faqJsonLd, softwareApplicationJsonLd } from "../lib/structuredData";
import type { Locale, ToolDefinition } from "../types";

type ToolPageProps = {
  tool: ToolDefinition;
  locale: Locale;
};

export function ToolPage({ tool, locale }: ToolPageProps) {
  const copy = getToolPageCopy(locale);
  const relatedTools = getRelatedTools(tool.id, locale);
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || "https://textpolish-tools.pages.dev";

  applyPageSeo({
    title: tool.seo.title,
    description: tool.seo.description,
    locale,
    path: tool.path,
    siteUrl
  });

  return (
    <main className="tool-page" id="main-content">
      <script type="application/ld+json">{JSON.stringify(softwareApplicationJsonLd(tool, siteUrl))}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd(tool))}</script>
      <section className="tool-hero">
        <div className="tool-intro">
          <p className="eyebrow">{categoryLabel(tool.category, locale)}</p>
          <h1>{tool.name}</h1>
          <p>{tool.description}</p>
          <div className="tool-intro__meta" aria-label={locale === "zh" ? "工具能力" : "Tool capabilities"}>
            <span>{locale === "zh" ? "本地指标" : "Local metrics"}</span>
            <span>{locale === "zh" ? "场景模板" : "Scenario templates"}</span>
            <span>{locale === "zh" ? "主流语种" : "Major languages"}</span>
            <span>{locale === "zh" ? "人工确认后复制" : "Review before copying"}</span>
          </div>
        </div>
        <ToolWorkspace tool={tool} locale={locale} />
      </section>
      <AdSlot label={copy.sponsored as string} className="wide-ad" />
      <section className="content-section">
        <h2>{(copy.howToTitle as string).replace("{tool}", tool.shortName)}</h2>
        <p>{copy.howToBody}</p>
      </section>
      <section className="content-section split-section">
        <div>
          <h2>{copy.bestForTitle}</h2>
          <ul>
            {(copy.bestForItems as string[]).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2>{copy.responsibleTitle}</h2>
          <p>{copy.responsibleBody}</p>
        </div>
      </section>
      <section className="content-section">
        <h2>{copy.relatedTitle}</h2>
        <div className="tool-grid tool-grid--compact">
          {relatedTools.map((relatedTool) => (
            <ToolCard key={relatedTool.id} tool={relatedTool} locale={locale} />
          ))}
        </div>
      </section>
      <section className="content-section faq-section">
        <h2>{copy.faqTitle}</h2>
        {tool.faqs.map((faq) => (
          <details key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
      </section>
    </main>
  );
}
